import { Card, CardContent } from "@/components/ui/card";
import { CalendarDays, TrendingUp, Clock, Timer } from "lucide-react";

interface ActivitiesSummaryCardsProps {
  todayCount: number;
  todayDonePercentage: number;
  thisWeekCount: number;
  lastWeekCount: number;
  timeSpentToday: number;
  timeSpentYesterday: number;
  timeSpentThisWeek: number;
  timeSpentLastWeek: number;
}

// Helper function to format minutes to hours and minutes
function formatTime(minutes: number): string {
  if (minutes === 0) return "0m";
  if (minutes < 60) return `${minutes}m`;
  
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  
  if (remainingMinutes === 0) return `${hours}h`;
  return `${hours}h ${remainingMinutes}m`;
}

export function ActivitiesSummaryCards({
  todayCount,
  todayDonePercentage,
  thisWeekCount,
  lastWeekCount,
  timeSpentToday,
  timeSpentYesterday,
  timeSpentThisWeek,
  timeSpentLastWeek,
}: ActivitiesSummaryCardsProps) {
  const weekComparisonPercentage = lastWeekCount > 0 
    ? ((thisWeekCount - lastWeekCount) / lastWeekCount * 100).toFixed(0)
    : 0;
  const isPositiveGrowth = Number(weekComparisonPercentage) >= 0;

  // Time comparison calculations
  const todayTimeComparisonPercentage = timeSpentYesterday > 0 
    ? ((timeSpentToday - timeSpentYesterday) / timeSpentYesterday * 100).toFixed(0)
    : timeSpentToday > 0 ? "100" : "0";
  const isTodayTimePositiveGrowth = Number(todayTimeComparisonPercentage) >= 0;

  const weekTimeComparisonPercentage = timeSpentLastWeek > 0 
    ? ((timeSpentThisWeek - timeSpentLastWeek) / timeSpentLastWeek * 100).toFixed(0)
    : timeSpentThisWeek > 0 ? "100" : "0";
  const isWeekTimePositiveGrowth = Number(weekTimeComparisonPercentage) >= 0;

  return (
    <div className="grid grid-cols-1 md:grid-cols-4 lg:grid-cols-4 gap-4 mb-6">
      <Card>
        <CardContent className="p-3">
          <div className="flex items-center space-x-2">
            <CalendarDays className="h-4 w-4 text-primary" />
            <div>
              <p className="text-xs text-muted-foreground">Activities Today</p>
              <p className="text-lg font-bold">{todayCount}</p>
              <p className="text-xs text-muted-foreground">
                {todayDonePercentage.toFixed(0)}% Done
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-3">
          <div className="flex items-center space-x-2">
            <TrendingUp className="h-4 w-4 text-primary" />
            <div>
              <p className="text-xs text-muted-foreground">Activities this week</p>
              <p className="text-lg font-bold">{thisWeekCount}</p>
              <p className="text-xs text-muted-foreground">
                {isPositiveGrowth && weekComparisonPercentage !== "0" && "+"}
                {weekComparisonPercentage !== "0" && `${weekComparisonPercentage}%`}
                {weekComparisonPercentage === "0" && "Same as"} vs last week
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-3">
          <div className="flex items-center space-x-2">
            <Clock className="h-4 w-4 text-blue-600" />
            <div>
              <p className="text-xs text-muted-foreground">Time spent Today</p>
              <p className="text-lg font-bold">{formatTime(timeSpentToday)}</p>
              <p className="text-xs text-muted-foreground">
                {isTodayTimePositiveGrowth && todayTimeComparisonPercentage !== "0" && "+"}
                {todayTimeComparisonPercentage !== "0" && `${todayTimeComparisonPercentage}%`}
                {todayTimeComparisonPercentage === "0" && "Same as"} vs yesterday
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-3">
          <div className="flex items-center space-x-2">
            <Timer className="h-4 w-4 text-green-600" />
            <div>
              <p className="text-xs text-muted-foreground">Time spent this week</p>
              <p className="text-lg font-bold">{formatTime(timeSpentThisWeek)}</p>
              <p className="text-xs text-muted-foreground">
                {isWeekTimePositiveGrowth && weekTimeComparisonPercentage !== "0" && "+"}
                {weekTimeComparisonPercentage !== "0" && `${weekTimeComparisonPercentage}%`}
                {weekTimeComparisonPercentage === "0" && "Same as"} vs last week
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}